import { RouterRootState } from "connected-react-router";
import { FC, ReactElement, ReactNode, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { isCloud } from "../../../constants/license-constants";
import { getCountsCategory, PROFILE_TYPE_CATEGORY, TYPE_PER_PRESET_TYPE } from "../../../constants/system-constants";
import BiometricPresetsTab from "../../../containers/biometric-preset-tab";
import ControllerPresetsTab from "../../../containers/controller-presets-tab";
import IODevicePresetsTab from "../../../containers/ioDevice-preset-tab";
import LockPresetsTab from "../../../containers/lock-presets-tab";
import ReaderPresetsTab from "../../../containers/reader-presets-tab";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Button } from "../../../components/button/Button";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import SystemStat from "../../../models/SystemStat";
import { useGlobalState } from "../../../Providers";
import { getSystemStats } from "../../../services/systemService";
import { showError } from "../../../services/utilities/ServiceError";

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

type HardwareProps = {
    selected: number;
    setSelected: (index: number) => void;
}

const HardwarePreset: FC<HardwareProps> = ({selected, setSelected}) => {
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([]);
    const [itemPaths, setItemPaths] = useState<string[]>([]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const { translate } = useLocale();
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const { buildInfo } = useGlobalState() ?? {};

    const isCloudMode = useMemo(() => {
        return isCloud(buildInfo?.usage);
    }, [buildInfo?.usage]);

    useEffect(() => {
        buildCategoriesData();
    }, [isCloudMode]);

    useEffect(() => {
        // This is being used so that each item set in categories we can monitor and make the correct path setup.
        setItemPaths(() => {
            const updated : string[] = [];
            categories.forEach(category => {
                const itemPath = findItemPath(category.displayName);
                if (itemPath && itemPath.length > 0) {
                    updated.push(itemPath);
                }
            });
            return updated;
        });
    }, [categories]);

    const findItemPath = (displayName: string): string => {
        switch (displayName) {
            case translate.byKey(TranslationKeys.controllerPreset):
                return '/access-control/hardware/controller-preset/';
            case translate.byKey(TranslationKeys.biometricPresets):
                return '/access-control/hardware/biometric-preset/';
            case translate.byKey(TranslationKeys.readerPreset):
                return '/access-control/hardware/reader-preset/';
            case translate.byKey(TranslationKeys.moduleAndIOPresets):
                return '/access-control/hardware/device-preset/';
            case translate.byKey(TranslationKeys.lockPresets):
                return '/access-control/hardware/lock-preset/';
            // case translate.byKey(TranslationKeys.lockerPresets):
            //     return '/access-control/hardware/locker-preset/';
            default:
                return '';
        }
    }

    const buildCategoriesData = async () => {
        try {
            setBusy(true);
            const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
            const systemStat: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_PRESET_TYPE) : undefined;
            let index = 1;
            
            setCategories([
                {
                    displayName: translate.byKey(TranslationKeys.controllerPreset),
                    heading: translate.byKey(TranslationKeys.controllerPreset),
                    value: "",
                    id: index,
                    count: systemStat ? getCountsCategory(systemStat, PROFILE_TYPE_CATEGORY.CONTROLLERS) : 0,
                },
                (!isCloudMode && (
                    {
                        displayName: translate.byKey(TranslationKeys.biometricPresets),
                        heading: translate.byKey(TranslationKeys.biometricPresets),
                        value: "",
                        id: index++,
                        count: systemStat ? getCountsCategory(systemStat, PROFILE_TYPE_CATEGORY.BIOMETRICS) : 0,
                    })
                ),
                {
                    displayName: translate.byKey(TranslationKeys.readerPreset),
                    heading: translate.byKey(TranslationKeys.readerPreset),
                    value: "",
                    id: index++,
                    count: systemStat ? getCountsCategory(systemStat, PROFILE_TYPE_CATEGORY.READERS) : 0,
                },
                {
                    displayName: translate.byKey(TranslationKeys.moduleAndIOPresets),
                    heading: translate.byKey(TranslationKeys.moduleAndIOPresets),
                    value: "",
                    id: index++,
                    count: systemStat ? getCountsCategory(systemStat, PROFILE_TYPE_CATEGORY.MODULES) : 0,
                },
                {
                    displayName: translate.byKey(TranslationKeys.lockPresets),
                    heading: translate.byKey(TranslationKeys.lockPresets),
                    value: "",
                    id: index++,
                    count: systemStat ? getCountsCategory(systemStat, PROFILE_TYPE_CATEGORY.LOCKS) : 0,

                },
                // {
                //     displayName: translate.byKey(TranslationKeys.lockerPresets),
                //     heading: translate.byKey(TranslationKeys.lockerPresets),
                //     value: "",
                //     id: index++,
                //     count: lockers.pagination.totalCount,
                // },
            ].filter(Boolean) as Category[]);
            setBusy(false);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent: ReactElement[] = [
                <ControllerPresetsTab setBusy={setBusy} categories={categories} setCategories={setCategories}></ControllerPresetsTab>,
                (!isCloudMode && <BiometricPresetsTab setBusy={setBusy} categories={categories} setCategories={setCategories}></BiometricPresetsTab>) || null,
                <ReaderPresetsTab setBusy={setBusy} categories={categories} setCategories={setCategories}></ReaderPresetsTab>,
                <IODevicePresetsTab setBusy={setBusy} categories={categories} setCategories={setCategories}></IODevicePresetsTab>,
                <LockPresetsTab setBusy={setBusy} categories={categories} setCategories={setCategories} />,
                // <LockerPresetsTab setBusy={setBusy} categories={categories} setCategories={setCategories} />
            ].filter(Boolean) as ReactElement[];
            setTabContent(newTabContent);
        }
    }, [categories]);

    /*
      Describes the route when clicking the Add button for each tab.
    */
    const getItempPath = (selected) => {
        return itemPaths[selected++];
    }

    return <ActivityFrame busy={busy} className="flex flex-grow overflow-y-hidden">
        <div id="container-tabs" className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
            <Breadcrumb
                path={[{
                    value: "/access-control/hardware",
                    displayName: translate.byKey(TranslationKeys.hardware),
                },
                {
                    value: "/access-control/hardware/presets",
                    displayName: translate.byKey(TranslationKeys.presets),
                },]}
                onClick={(item) => history.push(item.value)} />
            <Tabs tabStyle={{}}
                className="bg-white overflow-y-hidden"
                tabs={getTabsListFromLabels(categories)}
                onSelect={(i) => setSelected(i)} // TODO: We should be returning the ID from the category and not the placement index. This will require a rewrite.
                selected={selected}
                onAdd={() => { }}
                add={false}>
                {tabContent[selected]}
                <div className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                    <div className="flex flex-row justify-end button-cluster">
                        {getItempPath(selected) != null ?
                            <Restricted to={`add.${routeName}.element`}>
                                <Link to={{ pathname: getItempPath(selected), }}>
                                    <Button variant="submit" shape="rounded" id='presetsAddButton'>
                                        <img
                                            src={plusIcon}
                                            className="px-2 w-8 py-1"
                                            alt="" />
                                        <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                                    </Button>
                                </Link>
                             </Restricted> : null}
                    </div>
                </div>
            </Tabs>
        </div>
    </ActivityFrame>
};

export default HardwarePreset;
